import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { BottomSheet } from './ui/BottomSheet';
import { Dialog } from './ui/Dialog';
import { Button } from './ui/Button';
import { useIsDesktop } from '../hooks/useIsDesktop';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'default';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Bekräfta',
  cancelLabel = 'Avbryt',
  variant = 'danger',
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const isDesktop = useIsDesktop();
  const confirmClasses = variant === 'danger'
    ? 'bg-red-600 text-white'
    : 'bg-accent-600 text-white';

  // Desktop: a centred alert dialog, cancel first, the destructive action
  // last and red-text only so the eye lands on the safe choice.
  if (isDesktop) {
    return (
      <Dialog
        open={isOpen}
        onClose={onCancel}
        title={title}
        role="alertdialog"
        actions={
          <>
            <Button variant="secondary" onClick={onCancel} disabled={loading} data-autofocus>{cancelLabel}</Button>
            <Button variant={variant === 'danger' ? 'destructive' : 'primary'} onClick={onConfirm} loading={loading} className={variant === 'danger' ? 'border border-red-200 dark:border-red-800/40' : ''}>{confirmLabel}</Button>
          </>
        }
      >
        {message}
      </Dialog>
    );
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={onCancel} variant="alert">
      <div className="p-5 text-center">
        {variant === 'danger' && (
          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
        )}
        <h3 className="text-base font-semibold text-[var(--text-primary)]">{title}</h3>
        <p className="text-sm text-[var(--text-secondary)] mt-1">{message}</p>
      </div>

      {/* Button order is thumb-zone aware. In a bottom sheet the LAST child sits
          lowest and is therefore easiest to hit one-handed. For destructive
          actions that must be the safe choice: these deletes cascade (removing a
          room takes its items and photos) and only projects have a trash to
          restore from, so a mis-tap is unrecoverable. Non-destructive dialogs
          keep confirm-last so the primary action stays in the easy position. */}
      <div className={`flex gap-2 px-5 pb-5 ${variant === 'danger' ? 'flex-col-reverse' : 'flex-col'}`}>
        <button
          onClick={onConfirm}
          disabled={loading}
          className={`w-full py-3 px-4 text-[15px] font-medium min-h-[44px] rounded-xl transition-colors disabled:opacity-50 ${confirmClasses}`}
        >
          {loading ? 'Vänta...' : confirmLabel}
        </button>
        <button
          onClick={onCancel}
          disabled={loading}
          className="w-full py-3 px-4 text-[15px] font-medium min-h-[44px] rounded-xl text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)] active:bg-[var(--bg-surface-hover)] transition-colors disabled:opacity-50"
        >
          {cancelLabel}
        </button>
      </div>
    </BottomSheet>
  );
};

export default ConfirmDialog;
